// Bài giảng MẪU — Bài 1: Dao động điều hoà (3 phần, đủ loại thẻ) để học cách dùng.
import type { ContentItem } from '../../../types'
import type { LessonDoc } from '../types/LessonDoc'
import { genId } from './id'
import { makeSection } from './sections'

const SHM_HTML = `<!DOCTYPE html><html lang="vi"><head><meta charset="UTF-8"/>
<style>body{font-family:system-ui,sans-serif;margin:0;padding:12px;background:#0f172a;color:#e2e8f0}
canvas{width:100%;height:160px;background:#020617;border-radius:8px}label{font-size:13px}input{width:100%}</style></head>
<body><canvas id="c" width="600" height="160"></canvas>
<label>Biên độ A = <b id="v">3</b> cm</label><input id="A" type="range" min="1" max="6" step="0.5" value="3"/>
<script>var cv=document.getElementById('c'),x=cv.getContext('2d'),A=3,t0=Date.now();
document.getElementById('A').oninput=function(e){A=+e.target.value;document.getElementById('v').textContent=A};
function d(){var t=(Date.now()-t0)/1000,w=cv.width,h=cv.height,m=h/2,s=18;x.clearRect(0,0,w,h);
x.strokeStyle='#334155';x.beginPath();x.moveTo(0,m);x.lineTo(w,m);x.stroke();
x.strokeStyle='#38bdf8';x.lineWidth=2;x.beginPath();
for(var i=0;i<w;i++){var tt=t-(w-i)/60,y=m-A*Math.cos(2*tt)*s;i?x.lineTo(i,y):x.moveTo(i,y)}x.stroke();
requestAnimationFrame(d)}d();</script></body></html>`

const RICH_ITEMS: ContentItem[] = [
  { type: 'heading', content: 'Dao động điều hoà là gì?', level: 2 },
  {
    type: 'text',
    content:
      'Dao động điều hoà là dao động trong đó li độ của vật là hàm cosin (hoặc sin) theo thời gian: ' +
      '$x = A\\cos(\\omega t + \\varphi)$.',
  },
  { type: 'formula', content: '\\omega = 2\\pi f = \\frac{2\\pi}{T}', explanation: 'Tần số góc (rad/s)' } as ContentItem,
  {
    type: 'list',
    style: 'bullet',
    items: [
      '**A**: biên độ — li độ cực đại (luôn dương)',
      '**T**: chu kì — thời gian thực hiện một dao động toàn phần',
      '**f**: tần số — số dao động trong 1 giây',
      '**φ**: pha ban đầu — xác định trạng thái lúc t = 0',
    ],
  } as ContentItem,
  {
    type: 'table',
    headers: ['Đại lượng', 'Công thức', 'Đơn vị'],
    rows: [
      ['Tần số góc', '$\\omega = 2\\pi/T$', 'rad/s'],
      ['Vận tốc', '$v = -\\omega A\\sin(\\omega t+\\varphi)$', 'cm/s'],
      ['Gia tốc', '$a = -\\omega^2 x$', 'm/s²'],
    ],
  } as ContentItem,
]

export function buildSampleBai1(): LessonDoc {
  return {
    id: genId('lesson'),
    slug: '',
    meta: {
      tieuDe: 'Bài 1. Dao động điều hoà (Bài mẫu)',
      moTa: 'Bài giảng mẫu minh hoạ cấu trúc 3 phần và các loại thẻ.',
      lop: '11',
      chuong: '1',
      bai: '1',
      thoiGian: '45 phút',
    },
    status: 'draft',
    sections: [
      makeSection('Hoạt động mở đầu', [
        {
          id: genId(),
          type: 'text',
          title: 'Khởi động',
          content:
            'Quan sát chuyển động của con lắc lò xo. **Vị trí của vật thay đổi thế nào theo thời gian?** ' +
            'Kéo thanh trượt bên dưới để khám phá đồ thị li độ.',
        },
        { id: genId(), type: 'html', title: 'Mô phỏng dao động', htmlSource: SHM_HTML, height: 240 },
      ]),
      makeSection('Hoạt động chính', [
        { id: genId(), type: 'rich', title: 'Lý thuyết & công thức', items: RICH_ITEMS },
      ]),
      makeSection('Hoạt động kết thúc', [
        {
          id: genId(),
          type: 'quiz',
          title: 'Câu hỏi củng cố',
          source: 'manual',
          questions: [
            {
              type: 'multiple_choice',
              question: 'Vật dao động từ x = −8 cm đến x = +8 cm. Biên độ A bằng?',
              options: ['8 cm', '16 cm', '4 cm', '−8 cm'],
              correct: 0,
              explanation: 'A là li độ cực đại = 8 cm và luôn dương.',
              points: 0.25,
            },
            {
              type: 'true_false',
              question: 'Gia tốc trong dao động điều hoà luôn ngược dấu với li độ.',
              correct: true,
              explanation: 'Vì $a = -\\omega^2 x$ nên a và x luôn ngược dấu.',
              points: 1,
            },
            {
              type: 'fill_blank',
              question: 'Đơn vị của tần số góc ω là gì?',
              answer: 'rad/s',
              alternatives: ['rad/s', 'radian trên giây'],
              explanation: 'ω đo bằng rad/s.',
              points: 0.25,
            },
          ],
        },
        { id: genId(), type: 'game', title: 'Trò chơi ôn tập', game: 'doorquest', lop: 11, chuong: 1, bai: 1, maxQuestions: 10 },
      ]),
    ],
  }
}
